'use client';
import { useRef } from 'react';
import { motion, useInView } from 'motion/react';
import { ArrowRight, Clock, Shield, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useRouter } from 'next/navigation';

export default function CTA() {
  const router = useRouter();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="relative py-24 lg:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-mesh-hero" />

      {/* Decorative elements */}
      <div className="absolute top-1/2 left-0 w-[600px] h-[600px] -translate-y-1/2 -translate-x-1/2">
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--amber))]/10 to-transparent rounded-full blur-3xl" />
      </div>
      <div className="absolute top-1/2 right-0 w-[400px] h-[400px] -translate-y-1/2 translate-x-1/2">
        <div className="absolute inset-0 bg-gradient-to-l from-[hsl(var(--teal))]/10 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-5xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          {/* Main CTA Card */}
          <div className="relative bg-white rounded-3xl shadow-2xl border border-[hsl(var(--border))] overflow-hidden">
            {/* Accent gradient border effect */}
            <div className="absolute inset-0 rounded-3xl">
              <div className="absolute inset-[-1px] bg-gradient-to-r from-[hsl(var(--amber))]/50 via-transparent to-[hsl(var(--teal))]/50 rounded-3xl opacity-30" />
            </div>

            <div className="relative p-8 lg:p-16">
              {/* Content */}
              <div className="text-center max-w-2xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ delay: 0.2 }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[hsl(var(--amber))]/10 border border-[hsl(var(--amber))]/20 text-sm font-medium text-[hsl(var(--amber))] mb-6"
                >
                  <Clock className="w-4 h-4" />
                  Start in under 5 minutes
                </motion.div>

                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.3 }}
                  className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-[hsl(var(--slate-deep))] mb-6 text-balance"
                >
                  Stop Guessing.{' '}
                  <span className="text-gradient-amber">Start Knowing.</span>
                </motion.h2>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.4 }}
                  className="text-lg text-[hsl(var(--slate-medium))] mb-8 leading-relaxed"
                >
                  Your next board meeting deserves better than "it's a high-risk
                  system." Get precise financial figures that drive action.
                </motion.p>

                {/* CTA Buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.5 }}
                  className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8"
                >
                  <Button
                    size="lg"
                    onClick={() => router.push('/register')}
                    className="w-full sm:w-auto bg-gradient-to-r from-[hsl(var(--amber))] to-[hsl(var(--amber-light))] hover:from-[hsl(var(--amber-light))] hover:to-[hsl(var(--amber))] text-white shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 group px-8 h-14 text-base font-semibold"
                  >
                    Create Free Account
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => router.push('/login')}
                    className="w-full sm:w-auto border-[hsl(var(--slate-dark))]/20 text-[hsl(var(--slate-dark))] hover:bg-[hsl(var(--slate-dark))]/5 hover:border-[hsl(var(--slate-dark))]/40 h-14 px-8 text-base"
                  >
                    Schedule Demo
                  </Button>
                </motion.div>

                {/* Trust signals */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.6 }}
                  className="flex flex-wrap items-center justify-center gap-6 text-sm text-[hsl(var(--slate-medium))]"
                >
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    No credit card required
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    30-45 min assessment
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    SOC 2 compliant
                  </div>
                </motion.div>
              </div>
            </div>
          </div>

          {/* Floating elements */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.7 }}
            className="absolute -top-6 -left-6 lg:-top-8 lg:-left-8"
          >
            <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-2xl bg-gradient-to-br from-[hsl(var(--amber))] to-[hsl(var(--amber-light))] shadow-xl flex items-center justify-center animate-float-slow">
              <span className="text-2xl lg:text-3xl font-mono font-bold text-white">
                £
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.8 }}
            className="absolute -bottom-4 -right-4 lg:-bottom-6 lg:-right-6"
          >
            <div
              className="w-14 h-14 lg:w-16 lg:h-16 rounded-xl bg-gradient-to-br from-[hsl(var(--teal))] to-[hsl(var(--teal-light))] shadow-xl flex items-center justify-center animate-float"
              style={{ animationDelay: '1s' }}
            >
              <span className="text-xl lg:text-2xl font-mono font-bold text-white">
                %
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
